import React, { useState } from 'react';
import { CommandCenterLayout } from '../../components/layout/CommandCenterLayout';
import { DataTable } from '../../components/ui/DataTable';
import { StatusBadge, StatusType } from '../../components/ui/StatusBadge';
import { Button } from '../../components/ui/Button';
import { FilterChip } from '../../components/ui/FilterChip';
import { Eye, MessageSquare, CheckCircle, AlertTriangle, Clock, MapPin, Image as ImageIcon, Send, MoreHorizontal, User, Smartphone, Radio, ArrowRight, FileText, Upload } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { KPICard } from '../../components/ui/KPICard';
import { Select } from '../../components/ui/Select';
// Mock Data
const mockIssues = [{
  id: 'TKT-8821',
  category: 'Tumpukan Sampah Liar',
  location: 'Kota Bandung, Kec. Coblong',
  source: 'warga',
  date: '03 Feb 2025, 09:30',
  urgency: 'High',
  status: 'new',
  description: 'Tumpukan sampah liar di pinggir jalan mengganggu lalu lintas.',
  coordinates: '-6.8915, 107.6107',
  timeline: [{
    time: '03 Feb, 09:30',
    action: 'Laporan masuk',
    user: 'Warga (App)'
  }]
}, {
  id: 'TKT-8820',
  category: 'Pencemaran Sungai',
  location: 'Kab. Bekasi, Cikarang',
  source: 'officer',
  date: '03 Feb 2025, 08:15',
  urgency: 'Urgent',
  status: 'in_review',
  description: 'Limbah cair hitam pekat di sungai Cikarang.',
  coordinates: '-6.2415, 107.1407',
  timeline: [{
    time: '03 Feb, 08:15',
    action: 'Laporan masuk',
    user: 'Officer DLH'
  }, {
    time: '03 Feb, 08:30',
    action: 'Triage: Urgent',
    user: 'Admin Pusat'
  }]
}, {
  id: 'TKT-8819',
  category: 'Pembakaran Sampah',
  location: 'Kota Depok',
  source: 'listening',
  date: '02 Feb 2025, 16:45',
  urgency: 'Medium',
  status: 'dispatched',
  description: 'Viral di medsos pembakaran sampah massal.',
  coordinates: '-6.3915, 106.7407',
  timeline: [{
    time: '02 Feb, 16:45',
    action: 'Detected by AI',
    user: 'System'
  }, {
    time: '02 Feb, 17:00',
    action: 'Dispatched to DLH Depok',
    user: 'Admin Pusat'
  }]
}, {
  id: 'TKT-8818',
  category: 'Fasilitas Rusak',
  location: 'Jakarta Selatan',
  source: 'warga',
  date: '01 Feb 2025, 10:00',
  urgency: 'Low',
  status: 'closed',
  description: 'Tong sampah pecah.',
  coordinates: '-6.2215, 106.8507',
  timeline: [{
    time: '01 Feb, 10:00',
    action: 'Laporan masuk',
    user: 'Warga'
  }, {
    time: '01 Feb, 14:00',
    action: 'Selesai',
    user: 'Officer'
  }]
}];
export function IsuKrisisPage() {
  const [activeTab, setActiveTab] = useState('new');
  const [selectedIssue, setSelectedIssue] = useState<any | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Form States
  const [triageUrgency, setTriageUrgency] = useState('Medium');
  const [dispatchTarget, setDispatchTarget] = useState('');
  const [dispatchSLA, setDispatchSLA] = useState('');
  const [closureNote, setClosureNote] = useState('');
  const filteredData = mockIssues.filter(item => (activeTab === 'all' || item.status === activeTab) && (item.category.toLowerCase().includes(searchTerm.toLowerCase()) || item.location.toLowerCase().includes(searchTerm.toLowerCase()) || item.id.toLowerCase().includes(searchTerm.toLowerCase())));
  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'warga':
        return <Smartphone className="h-4 w-4 text-blue-500" data-id="element-1422" />;
      case 'officer':
        return <User className="h-4 w-4 text-green-500" data-id="element-1423" />;
      case 'listening':
        return <Radio className="h-4 w-4 text-purple-500" data-id="element-1424" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-500" data-id="element-1425" />;
    }
  };
  const getUrgencyBadge = (urgency: string) => {
    switch (urgency) {
      case 'Urgent':
        return <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded font-bold" data-id="element-1426">
            URGENT
          </span>;
      case 'High':
        return <span className="bg-orange-100 text-orange-800 text-xs px-2 py-0.5 rounded font-medium" data-id="element-1427">
            High
          </span>;
      case 'Medium':
        return <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded font-medium" data-id="element-1428">
            Medium
          </span>;
      case 'Low':
        return <span className="bg-gray-100 text-gray-800 text-xs px-2 py-0.5 rounded font-medium" data-id="element-1429">
            Low
          </span>;
      default:
        return null;
    }
  };
  const columns = [{
    header: 'ID Tiket',
    cell: (row: any) => <div className="flex items-center gap-2" data-id="element-1430">
          {getSourceIcon(row.source)}
          <span className="font-mono text-xs font-medium" data-id="element-1431">{row.id}</span>
        </div>
  }, {
    header: 'Kategori',
    accessorKey: 'category' as const
  }, {
    header: 'Lokasi',
    accessorKey: 'location' as const
  }, {
    header: 'Urgensi',
    cell: (row: any) => getUrgencyBadge(row.urgency)
  }, {
    header: 'Waktu',
    accessorKey: 'date' as const,
    className: 'text-xs text-gray-500'
  }, {
    header: 'Aksi',
    cell: (row: any) => <Button variant="ghost" size="sm" className="text-primary-600" onClick={() => setSelectedIssue(row)} data-id="element-1432">
          Proses
        </Button>
  }];
  return <CommandCenterLayout title="Manajemen Isu & Krisis" subtitle="Workflow penanganan isu lingkungan dari pelaporan hingga penyelesaian" data-id="element-1433">
      {/* KPI Cards - Responsive Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6" data-id="element-1434">
        <KPICard title="Isu Masuk (24h)" value="42" icon={AlertTriangle} trend={{
        value: '+5 vs kemarin',
        direction: 'up'
      }} data-id="element-1435" />
        <KPICard title="Menunggu Triage" value="15" icon={Clock} className="border-l-4 border-l-red-500" data-id="element-1436" />
        <KPICard title="Dispatched" value="28" icon={Send} className="border-l-4 border-l-blue-500" data-id="element-1437" />
        <KPICard title="Selesai Hari Ini" value="12" icon={CheckCircle} className="border-l-4 border-l-green-500" data-id="element-1438" />
      </div>

      <div className="flex flex-col space-y-4 mb-6" data-id="element-1439">
        {/* Tabs - Scrollable on Mobile */}
        <div className="flex gap-2 border-b pb-0 overflow-x-auto no-scrollbar" data-id="element-1440">
          {[{
          id: 'new',
          label: 'New (Untriaged)'
        }, {
          id: 'in_review',
          label: 'In Review'
        }, {
          id: 'dispatched',
          label: 'Dispatched'
        }, {
          id: 'in_progress',
          label: 'In Progress'
        }, {
          id: 'closed',
          label: 'Closed'
        }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-1441">
              {tab.label}
            </button>)}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center" data-id="element-1442">
          <div className="flex flex-wrap gap-2 items-center" data-id="element-1443">
            <FilterChip label="Urgent Only" active={false} onClick={() => {}} data-id="element-1444" />
            <FilterChip label="Source: Warga" active={false} onClick={() => {}} data-id="element-1445" />
          </div>

          <div className="relative w-full md:w-64" data-id="element-1446">
            <Input placeholder="Cari ID tiket atau lokasi..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1447" />
          </div>
        </div>
      </div>

      {/* DataTable Wrapper for Horizontal Scroll */}
      <div className="overflow-x-auto -mx-4 md:mx-0 px-4 md:px-0" data-id="element-1448">
        <DataTable data={filteredData} columns={columns} keyExtractor={item => item.id} onRowClick={item => setSelectedIssue(item)} data-id="element-1449" />
      </div>

      {/* Issue Detail Modal */}
      <Modal isOpen={!!selectedIssue} onClose={() => setSelectedIssue(null)} title={`Tiket #${selectedIssue?.id}`} size="2xl" data-id="element-1450">
        {selectedIssue && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1451">
            {/* Left Column: Info & Evidence */}
            <div className="lg:col-span-2 space-y-6" data-id="element-1452">
              <div className="p-4 bg-gray-50 rounded-lg border" data-id="element-1453">
                <div className="flex justify-between items-start mb-2" data-id="element-1454">
                  <h3 className="font-bold text-lg" data-id="element-1455">
                    {selectedIssue.category}
                  </h3>
                  {getUrgencyBadge(selectedIssue.urgency)}
                </div>
                <p className="text-gray-600 text-sm mb-4" data-id="element-1456">
                  {selectedIssue.description}
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-1457">
                  <MapPin className="h-3 w-3" data-id="element-1458" /> {selectedIssue.location}
                </div>
              </div>

              {/* Evidence */}
              <div data-id="element-1459">
                <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-1460">
                  Bukti Foto
                </h4>
                <div className="grid grid-cols-3 gap-2" data-id="element-1461">
                  {[1, 2].map(i => <div key={i} className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center border" data-id="element-1462">
                      <ImageIcon className="h-6 w-6 text-gray-400" data-id="element-1463" />
                    </div>)}
                </div>
              </div>

              {/* Timeline */}
              <div data-id="element-1464">
                <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-1465">
                  Audit Trail
                </h4>
                <div className="space-y-4 relative pl-4 border-l" data-id="element-1466">
                  {selectedIssue.timeline.map((event: any, i: number) => <div key={i} className="relative" data-id="element-1467">
                      <div className="absolute -left-[21px] top-1 h-3 w-3 rounded-full bg-primary-500 border-2 border-white" data-id="element-1468"></div>
                      <p className="text-sm font-medium text-gray-900" data-id="element-1469">
                        {event.action}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-1470">
                        {event.time} • {event.user}
                      </p>
                    </div>)}
                </div>
              </div>
            </div>

            {/* Right Column: Workflow Actions */}
            <div className="space-y-6" data-id="element-1471">
              {/* Triage Panel */}
              {selectedIssue.status === 'new' && <div className="bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-1472">
                  <h4 className="font-bold text-blue-900 mb-3 flex items-center gap-2" data-id="element-1473">
                    <AlertTriangle className="h-4 w-4" data-id="element-1474" /> Triage
                  </h4>
                  <div className="space-y-3" data-id="element-1475">
                    <div data-id="element-1476">
                      <label className="text-xs font-medium text-gray-600" data-id="element-1477">
                        Set Urgensi
                      </label>
                      <Select value={triageUrgency} onChange={e => setTriageUrgency(e.target.value)} data-id="element-1478">
                        <option data-id="element-1479">Low</option>
                        <option data-id="element-1480">Medium</option>
                        <option data-id="element-1481">High</option>
                        <option data-id="element-1482">Urgent</option>
                      </Select>
                    </div>
                    <Button className="w-full bg-blue-600 text-white" data-id="element-1483">
                      Simpan Triage
                    </Button>
                  </div>
                </div>}

              {/* Dispatch Panel */}
              {(selectedIssue.status === 'in_review' || selectedIssue.status === 'new') && <div className="bg-purple-50 p-4 rounded-lg border border-purple-100" data-id="element-1484">
                  <h4 className="font-bold text-purple-900 mb-3 flex items-center gap-2" data-id="element-1485">
                    <Send className="h-4 w-4" data-id="element-1486" /> Dispatch
                  </h4>
                  <div className="space-y-3" data-id="element-1487">
                    <div data-id="element-1488">
                      <label className="text-xs font-medium text-gray-600" data-id="element-1489">
                        Target Instansi
                      </label>
                      <Select value={dispatchTarget} onChange={e => setDispatchTarget(e.target.value)} data-id="element-1490">
                        <option value="" data-id="element-1491">Pilih Instansi...</option>
                        <option data-id="element-1492">DLH Provinsi Jawa Barat</option>
                        <option data-id="element-1493">DLH Kota Bandung</option>
                      </Select>
                    </div>
                    <div data-id="element-1494">
                      <label className="text-xs font-medium text-gray-600" data-id="element-1495">
                        Set SLA (Batas Waktu)
                      </label>
                      <Input type="date" value={dispatchSLA} onChange={e => setDispatchSLA(e.target.value)} data-id="element-1496" />
                    </div>
                    <Button className="w-full bg-purple-600 text-white" data-id="element-1497">
                      Dispatch Tiket
                    </Button>
                  </div>
                </div>}

              {/* Closure Panel */}
              {selectedIssue.status === 'in_progress' && <div className="bg-green-50 p-4 rounded-lg border border-green-100" data-id="element-1498">
                  <h4 className="font-bold text-green-900 mb-3 flex items-center gap-2" data-id="element-1499">
                    <CheckCircle className="h-4 w-4" data-id="element-1500" /> Penyelesaian
                  </h4>
                  <div className="space-y-3" data-id="element-1501">
                    <div data-id="element-1502">
                      <label className="text-xs font-medium text-gray-600" data-id="element-1503">
                        Upload Bukti
                      </label>
                      <div className="flex gap-2" data-id="element-1504">
                        <Button variant="outline" size="sm" className="w-full" data-id="element-1505">
                          <Upload className="h-3 w-3 mr-1" data-id="element-1506" /> Foto
                        </Button>
                      </div>
                    </div>
                    <div data-id="element-1507">
                      <label className="text-xs font-medium text-gray-600" data-id="element-1508">
                        Catatan
                      </label>
                      <Textarea placeholder="Jelaskan penyelesaian..." value={closureNote} onChange={e => setClosureNote(e.target.value)} data-id="element-1509" />
                    </div>
                    <Button className="w-full bg-green-600 text-white" data-id="element-1510">
                      Tutup Tiket
                    </Button>
                  </div>
                </div>}
            </div>
          </div>}
      </Modal>
    </CommandCenterLayout>;
}